import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsLogged } from '../../store/auth/authReducer';
import { missionsHubRequest, missionsModalClosed } from '../../store/missions/missionsActions';
import {
	selectMissionsCycleKey,
	selectMissionsHubErrorKey,
	selectMissionsHubLoading,
	selectMissionsModalOpen,
	selectMissionsResetAt,
} from '../../store/missions/missionsReducer';
import { navigationRef } from '../../utils/helpers/navigationService';
import DailyMissionsModal from '../Components/Modals/DailyMissionsModal';

const LOAD_RETRY_BASE_MS = 30000;
const LOAD_RETRY_MAX_MS = 300000;
const ROLLOVER_RETRY_DELAY_MS = 60000;
const ROLLOVER_GRACE_MS = 1000;
const STALE_RESET_LIMIT_MS = 6 * 60 * 60 * 1000;

const DailyMissionsHost = () => {
	const dispatch = useDispatch();
	const isLogged = useSelector(selectIsLogged);
	const modalOpen = useSelector(selectMissionsModalOpen);
	const cycleKey = useSelector(selectMissionsCycleKey);
	const resetAt = useSelector(selectMissionsResetAt);
	const hubLoading = useSelector(selectMissionsHubLoading);
	const hubErrorKey = useSelector(selectMissionsHubErrorKey);
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [rolloverAttempt, setRolloverAttempt] = useState(0);

	useEffect(() => {
		if (!isLogged || cycleKey !== null || hubLoading || hubErrorKey) return;

		dispatch(missionsHubRequest());
	}, [isLogged, cycleKey, hubLoading, hubErrorKey, dispatch]);

	useEffect(() => {
		if (cycleKey !== null) setLoadAttempt(0);
	}, [cycleKey]);

	useEffect(() => {
		if (!isLogged || cycleKey !== null || hubLoading || !hubErrorKey) return;

		const backoff = Math.min(LOAD_RETRY_BASE_MS * 2 ** loadAttempt, LOAD_RETRY_MAX_MS);
		const timeout = setTimeout(
			() => {
				setLoadAttempt(attempt => attempt + 1);
				dispatch(missionsHubRequest());
			},
			backoff * (0.8 + Math.random() * 0.4),
		);

		return () => clearTimeout(timeout);
	}, [isLogged, cycleKey, hubLoading, hubErrorKey, loadAttempt, dispatch]);

	useEffect(() => {
		if (!modalOpen || !isLogged) return;

		dispatch(missionsHubRequest());
	}, [modalOpen, isLogged, dispatch]);

	useEffect(() => {
		if (!isLogged || !resetAt) return;

		const deadline = Date.parse(resetAt);
		if (Number.isNaN(deadline)) return;

		const remaining = deadline - Date.now();
		if (remaining < -STALE_RESET_LIMIT_MS) return;

		const timeout = setTimeout(
			() => {
				dispatch(missionsHubRequest());
				setRolloverAttempt(attempt => attempt + 1);
			},
			remaining > 0 ? remaining + ROLLOVER_GRACE_MS : ROLLOVER_RETRY_DELAY_MS,
		);

		return () => clearTimeout(timeout);
	}, [isLogged, resetAt, rolloverAttempt, dispatch]);

	useEffect(() => {
		if (!modalOpen) return;

		const openedRouteKey = navigationRef.isReady()
			? navigationRef.getCurrentRoute()?.key
			: undefined;
		if (!openedRouteKey) return;

		return navigationRef.addListener('state', () => {
			if (navigationRef.getCurrentRoute()?.key !== openedRouteKey) {
				dispatch(missionsModalClosed());
			}
		});
	}, [modalOpen, dispatch]);

	return <DailyMissionsModal visible={modalOpen} onClose={() => dispatch(missionsModalClosed())} />;
};

export default DailyMissionsHost;
